import { useState } from 'react';
import {
  Bell,
  BookOpen,
  Clock,
  FileText,
  Lock,
  SignOut,
  Moon,
  ChartPie,
  ArrowClockwise,
  Shield,
  Trash,
  User,
} from '@phosphor-icons/react';
import { useLoaderData } from 'react-router-dom';

import { LogoutConfirmDialog } from '../../components/settings/LogoutConfirmDialog';
import { SettingsAccountHeader } from '../../components/settings/SettingsAccountHeader';
import { SettingsGroup } from '../../components/settings/SettingsGroup';
import { SettingsRow, SettingsRowDivider } from '../../components/settings/SettingsRow';
import { ThemeSegmentControl } from '../../components/settings/ThemeSegmentControl';
import { DESKTOP_PAGE_HEADER_CLASS } from '../../components/layout/mobileLayoutConstants';
import { settingsHubLoader } from '../../data/settings';
import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { useAuth } from '../../store/AuthContext';
import { clampSelfieDay } from '../../utils/guidedFlowUtils';

import packageJson from '../../../package.json';

function methodLabel(activeMethod) {
  if (!activeMethod?.name) return pt.settings.hub.budgetMethod.emptyValue;
  return activeMethod.name;
}

export function SettingsOverview() {
  const loaderData = useLoaderData();
  const { user: authUser, profile: authProfile } = useAuth();
  const user = loaderData?.user || authUser;
  const profile = user?.profile || authProfile;
  const activeMethod = loaderData?.activeMethod;
  const cycleDay = clampSelfieDay(profile?.selfie_day_of_month ?? 1);

  const [logoutOpen, setLogoutOpen] = useState(false);

  return (
    <div className="mx-auto max-w-2xl space-y-5 md:space-y-6">
      <header className={DESKTOP_PAGE_HEADER_CLASS}>
        <h1 className="text-2xl font-semibold text-foreground">{pt.settings.title}</h1>
        <p className="mt-1 text-muted-foreground">{pt.settings.subtitle}</p>
      </header>

      <SettingsAccountHeader profile={profile} user={user} />

      <SettingsGroup title={pt.settings.hub.groups.account}>
        <SettingsRow
          icon={User}
          title={pt.settings.hub.profile.title}
          description={pt.settings.hub.profile.description}
          to={paths.settingsProfile}
        />
        <SettingsRowDivider />
        <SettingsRow
          icon={Lock}
          title={pt.settings.hub.account.title}
          to={paths.settingsAccount}
        />
      </SettingsGroup>

      <SettingsGroup title={pt.settings.hub.groups.notifications}>
        <SettingsRow
          icon={Bell}
          title={pt.settings.hub.notifications.title}
          description={pt.settings.hub.notifications.description}
          to={paths.settingsNotifications}
        />
        <SettingsRowDivider />
        <SettingsRow
          icon={Clock}
          title={pt.settings.hub.reminders.title}
          description={pt.settings.hub.reminders.description}
          to={paths.settingsReminders}
        />
      </SettingsGroup>

      <SettingsGroup title={pt.settings.hub.groups.personalisation}>
        <SettingsRow
          icon={ChartPie}
          title={pt.settings.hub.budgetMethod.title}
          description={pt.settings.hub.budgetMethod.description}
          badge={pt.settings.hub.shortcutBadge}
          value={methodLabel(activeMethod)}
          to={paths.settingsBudgetMethod}
        />
        <SettingsRowDivider />
        <SettingsRow
          icon={ArrowClockwise}
          title={pt.settings.hub.cycleDay.title}
          badge={pt.settings.hub.shortcutBadge}
          value={pt.settings.hub.cycleDay.value.replace('{day}', String(cycleDay))}
          to={paths.settingsCycleDay}
        />
        <SettingsRowDivider />
        <SettingsRow
          icon={Moon}
          title={pt.settings.hub.appearance.title}
          rightSlot={<ThemeSegmentControl />}
        />
      </SettingsGroup>

      <SettingsGroup title={pt.settings.hub.groups.help}>
        <SettingsRow
          icon={BookOpen}
          title={pt.settings.hub.help.title}
          description={pt.settings.hub.help.description}
          to={paths.settingsHelp}
        />
        <SettingsRowDivider />
        <SettingsRow icon={FileText} title={pt.settings.hub.terms.title} to="/terms" />
        <SettingsRowDivider />
        <SettingsRow icon={Shield} title={pt.settings.hub.privacy.title} to="/privacy" />
      </SettingsGroup>

      <SettingsGroup>
        <SettingsRow
          icon={SignOut}
          title={pt.settings.hub.logout.title}
          variant="logout"
          showChevron={false}
          onClick={() => setLogoutOpen(true)}
        />
        <SettingsRowDivider />
        <SettingsRow
          icon={Trash}
          title={pt.settings.hub.deleteAccount.title}
          description={pt.settings.hub.deleteAccount.description}
          variant="danger"
          to={paths.settingsDeleteAccount}
        />
      </SettingsGroup>

      <p className="text-center text-[11px] text-muted-foreground/80">
        {pt.settings.hub.version.replace('{version}', packageJson.version)}
      </p>

      <LogoutConfirmDialog open={logoutOpen} onOpenChange={setLogoutOpen} />
    </div>
  );
}

SettingsOverview.loader = settingsHubLoader;
